import { ID, CollectionQueryParams } from './common';
import type { Event } from './event';
import type { Occurrence } from './occurrence';
import { PaginationMeta } from './responses';

export enum VehicleType {
    BICYCLE = 'bicycle',
    CAR = 'car',
    TRUCK = 'truck',
    MOTORCYCLE = 'motorcycle',
    BUS = 'bus',
}

export interface Vehicle {
    id: ID;
    licensePlate: string;
    vehicleType: VehicleType;
    known: boolean;
    createdAt: number;
    updatedAt: number;
    lastSeen?: number;
    lastSourceName?: string;
    lastSourceType?: string;
    lastEventId?: string;
    lastEvent?: Event;
    name?: string | null;
    tags?: string[];
    thumbnailUrl?: string;
}

export interface VehicleOccurrence extends Occurrence {
    licensePlateId: string;
    text: string;
    vehicleId: ID;
}

export interface VehicleAnalyticsSourceStats {
    id: string;
    name: string;
    type: string;
    value: number;
    max: number;
}

export interface VehicleAnalyticsStats {
    totalEvents: number;
    totalSources: number;
    totalEventsOverall: number;
    chartData: Array<{
        date: string;
        value: number;
        startTime: number;
        endTime: number;
        sourceCounts: Array<{ id: string; value: number }>;
    }>;
    sourceStats: VehicleAnalyticsSourceStats[];
}

export interface VehiclePaginatedResponse {
    items: Vehicle[];
    page: PaginationMeta;
}

export type VehicleQueryParams = CollectionQueryParams & {
    known?: boolean;
    tags?: string[];
    vehicleTypes?: VehicleType[];
    startTime?: number;
    endTime?: number;
};

export interface VehicleSearchResult {
    vehicle: Vehicle;
    confidence: number;
}

export interface CreateVehiclePayload {
    licensePlate: string;
    vehicleType?: VehicleType;
    name?: string;
    tags?: string[];
}

export interface VehicleMergeSuggestion {
    vehicleId: string;
    licensePlate: string;
    name: string | null;
    imageUrl: string | null;
    lastSourceName: string | null;
    similarityScore: number;
    totalMatches: number;
    lastSeen: number | null;
}

export interface VehicleMergeTarget {
    vehicleId: string;
    licensePlate: string;
    name: string | null;
    imageUrl: string | null;
    lastSourceName: string | null;
    known: boolean;
    totalMatches: number;
    lastSeen: number | null;
}

export interface VehicleMergeSuggestionsResponse {
    suggestions: VehicleMergeSuggestion[];
}

export interface VehicleEventLinkResult {
    eventId: string;
    previousVehicleId: string;
    targetVehicleId: string;
}

export interface VehicleMergeSummary {
    sourceVehicleId: string;
    matchesTransferred: number;
    tagsTransferred: number;
}

export interface VehicleMergeResult {
    targetVehicle: Vehicle;
    mergeSummary: VehicleMergeSummary;
}

export interface MergeVehiclesPayload {
    sourceVehicleId: string;
    targetVehicleId: string;
}

export interface LinkVehiclePayload {
    targetVehicleId: string;
}

export interface UpdateVehiclePayload {
    licensePlate?: string;
    vehicleType?: VehicleType;
    name?: string | null;
    tags?: string[];
}
